import { ListTable, Money, type ListColumnDef } from '@bemor/web-kit/ui';

import type { FinanceReport, FinanceRow } from '@/api/types';

/* BM-122: the finance report's Table tab (Nexroll's `profit-report-table.tsx` on the kit's `ListTable`, rule 0.1):
   one row per period or dimension value, the totals as the last row, every figure the server's. */

const bps = (value: number | null) =>
    value == null ? '—' : `${(value / 100).toFixed(1)}%`;

const money = (cents: number | null) =>
    cents == null ? (
        <span className="text-fg-2">—</span>
    ) : (
        <Money cents={cents} currency="USD" />
    );

const count = (n: number) => <span className="tabular-nums">{n}</span>;

export const FINANCE_COLUMNS: ListColumnDef<FinanceRow>[] = [
    {
        key: 'label',
        label: 'Period',
        cell: (r) => r.label,
        locked: 'start',
        boardCard: true,
        noTruncate: true,
    },
    {
        key: 'orders',
        label: 'Orders',
        cell: (r) => count(r.orders),
        noTruncate: true,
    },
    {
        key: 'gross_cents',
        label: 'Gross',
        cell: (r) => money(r.gross_cents),
        noTruncate: true,
    },
    {
        key: 'refunded_cents',
        label: 'Refunded',
        cell: (r) => money(r.refunded_cents),
        noTruncate: true,
    },
    {
        key: 'chargebacks',
        label: 'Chargebacks',
        cell: (r) => count(r.chargebacks),
        noTruncate: true,
    },
    {
        key: 'chargebacks_cents',
        label: 'Chargebacks $',
        cell: (r) => money(r.chargebacks_cents),
        noTruncate: true,
    },
    {
        key: 'chargeback_fees_cents',
        label: 'Chargeback fees',
        cell: (r) => money(r.chargeback_fees_cents),
        noTruncate: true,
    },
    {
        key: 'income_cents',
        label: 'Fee income',
        cell: (r) => money(r.income_cents),
        boardCard: true,
        noTruncate: true,
    },
    {
        key: 'expected_cost_cents',
        label: 'Expected cost',
        cell: (r) => money(r.expected_cost_cents),
        noTruncate: true,
    },
    {
        key: 'actual_cost_cents',
        label: 'Actual cost',
        cell: (r) => money(r.actual_cost_cents),
        noTruncate: true,
    },
    {
        key: 'margin_expected_cents',
        label: 'Expected margin',
        cell: (r) => money(r.margin_expected_cents),
        noTruncate: true,
    },
    {
        key: 'margin_actual_cents',
        label: 'Actual margin',
        cell: (r) => money(r.margin_actual_cents),
        noTruncate: true,
    },
    {
        key: 'margin_actual_bps',
        label: 'Margin %',
        cell: (r) => bps(r.margin_actual_bps ?? r.margin_expected_bps),
        noTruncate: true,
    },
    {
        key: 'reserve_held_cents',
        label: 'Reserve held',
        cell: (r) => money(r.reserve_held_cents),
        noTruncate: true,
    },
    {
        key: 'reserve_released_cents',
        label: 'Reserve released',
        cell: (r) => money(r.reserve_released_cents),
        noTruncate: true,
    },
];

export function FinanceTable({
    report,
    by,
}: {
    report: FinanceReport;
    by: string;
}) {
    const columns = FINANCE_COLUMNS.map((c) =>
        c.key === 'label'
            ? {
                  ...c,
                  label:
                      {
                          period: 'Period',
                          merchant: 'Merchant',
                          store: 'Store',
                          card_brand: 'Card brand',
                          fee_rule: 'Fee rule',
                      }[by] ?? 'Key',
              }
            : c,
    );
    const rows = [
        ...report.rows,
        { ...report.totals, key: 'total', label: 'Total' },
    ];
    return (
        <div className="overflow-x-auto" data-testid="finance-table">
            <ListTable
                columns={columns}
                rows={rows}
                rowKey={(r) => r.key}
                stickyHeader={false}
                emptyState={
                    <p className="text-fg-2 p-4 text-center text-sm">
                        Nothing in this range.
                    </p>
                }
            />
        </div>
    );
}
